import { computed, effect, inject, untracked } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, switchMap } from 'rxjs';

import { setErrorStatusFor, setIdleStatusFor, setPendingStatusFor, setSuccessStatusFor, withRequestStatus } from '@drf/core';
import { WalletIdentityStore } from '@drf/entities/wallet';

import { PORTFOLIO_API } from '../api';
import { PortfolioActiveStrategy, PortfolioRequest, PortfolioResponse } from '../models';

interface PortfolioState {
  portfolio: PortfolioResponse | null;
}

const initialState: PortfolioState = {
  portfolio: null,
};

export const PortfolioStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(['getPortfolio']),
  withProps(store => ({
    portfolioApi: inject(PORTFOLIO_API),
    store,
    walletIdentityStore: inject(WalletIdentityStore),
  })),
  withMethods(({ portfolioApi, store, walletIdentityStore }) => {
    const loadPortfolioRequest = rxMethod<PortfolioRequest | null>(
      switchMap(request => {
        if (!request) {
          patchState(store, { portfolio: null }, setIdleStatusFor('getPortfolio'));

          return EMPTY;
        }

        patchState(store, setPendingStatusFor('getPortfolio'));

        return portfolioApi.getPortfolio(request).pipe(
          tapResponse({
            next: portfolio => {
              patchState(store, { portfolio }, setSuccessStatusFor('getPortfolio'));
            },
            error: error => {
              patchState(store, setErrorStatusFor('getPortfolio', toPortfolioErrorMessage(error)));
            },
          }),
        );
      }),
    );
    const clearPortfolio = (): void => {
      loadPortfolioRequest(null);
    };
    const getPortfolio = (request: PortfolioRequest): void => {
      loadPortfolioRequest(request);
    };
    const refreshPortfolio = (): void => {
      const identity = walletIdentityStore.identity();

      if (walletIdentityStore.isPending()) {
        clearPortfolio();
        patchState(store, setPendingStatusFor('getPortfolio'));

        return;
      }

      if (!walletIdentityStore.isConnected() || !identity) {
        clearPortfolio();

        return;
      }

      getPortfolio({
        chainId: identity.chainId,
        wallets: [
          {
            userAddress: identity.accountAddress,
          },
        ],
      });
    };

    return {
      clearPortfolio,
      getPortfolio,
      refreshPortfolio,
    };
  }),
  withHooks(({ refreshPortfolio, walletIdentityStore }) => ({
    onInit(): void {
      effect(() => {
        walletIdentityStore.needRefreshWalletData();

        untracked(refreshPortfolio);
      });
    },
  })),
  withComputed(({ portfolio }) => {
    const hasNonZeroAssets = (strategy: PortfolioActiveStrategy): boolean => strategy.assets > 0;
    const activeStrategies = computed(() =>
      (portfolio()?.activeStrategies ?? [])
        .filter(hasNonZeroAssets)
        .slice()
        .sort((a, b) => b.assetsAmountUSD - a.assetsAmountUSD),
    );
    return {
      activeStrategies,
      hasActiveStrategies: computed(() => activeStrategies().length > 0),
      portfolioSummary: computed(() => portfolio()?.portfolio ?? null),
    };
  }),
);

function toPortfolioErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Failed to load portfolio.';
}
